// "Create lead from call" pre-fill (contract ~/pm/contracts/B4.md v1.2 §6,
// GET calls/{id}/lead-prefill/). Pure, so it is testable without the barrel.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// The note is permanent text with no zone, so it is written in the practice's time.
const UK = 'Europe/London';

// Where a pre-filled value came from, as staff read it under the field.
export const PREFILL_SOURCE_TEXT = {
  call_number: 'From the call',
  call: 'From the call',
  call_line: 'From the line the call came in on',
  elevate_display_name: 'From the caller ID (Elevate)',
  address_book_name: 'From the Elevate contact book',
  address_book: 'From the Elevate contact book',
  transcript: 'From the call transcript',
  summary: 'From the call summary',
  ai_review: 'From the call review',
};
export const prefillSourceText = (source) => PREFILL_SOURCE_TEXT[source] || (source ? `From ${String(source).replace(/_/g, ' ')}` : '');

const field = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const value = typeof raw.value === 'string' ? raw.value.trim() : raw.value;
  if (value === undefined || value === null || value === '') return null;
  return { value, source: raw.source || '', sourceText: prefillSourceText(raw.source), label: typeof raw.label === 'string' ? raw.label : '' };
};

// null when there is nothing to use (older backend, or the endpoint refused).
export function prefillFields(prefill) {
  if (!prefill || typeof prefill !== 'object' || !prefill.fields) return null;
  const f = prefill.fields;
  const summary = prefill.summary && typeof prefill.summary === 'object' ? prefill.summary : {};
  const summarySource = summary.source || 'summary';
  const fromSummary = (value) => (typeof value === 'string' && value.trim() ? { value: value.trim(), source: summarySource, sourceText: prefillSourceText(summarySource) } : null);
  return {
    phone: field(f.phone),
    firstName: field(f.first_name),
    lastName: field(f.last_name),
    source: field(f.source),
    line: field(f.line),
    calledAt: field(f.called_at),
    // Filled by the transcripts/summaries backend (B7) later; null until then.
    treatmentInterest: fromSummary(summary.treatment_interest),
    callbackTime: fromSummary(summary.callback_time),
    linkCallIds: Array.isArray(prefill.link_call_ids) ? prefill.link_call_ids : [],
  };
}

// The pre-fill with the call row filling the gaps, so the dialog always has the
// number, line and call time even on an older backend (prefill = null). The line
// names the ring group ("Wokingham · Existing") when the row knows it. A name
// taken from Elevate's display name is dropped when that name is really the line.
export function withCallFallbacks(fields, row, { phone = '', line = '', nameIsLine = false } = {}) {
  const fromCall = (value, source) => (value ? { value, source, sourceText: prefillSourceText(source), label: '' } : null);
  const base = fields || {
    phone: null, firstName: null, lastName: null, source: null, line: null, calledAt: null, treatmentInterest: null, callbackTime: null, linkCallIds: [],
  };
  const lineName = (f) => (nameIsLine && f?.source === 'elevate_display_name' ? null : f);
  return {
    ...base,
    phone: base.phone || fromCall(phone, 'call_number'),
    firstName: lineName(base.firstName),
    lastName: lineName(base.lastName),
    line: line ? { ...(base.line || fromCall(line, 'call_line')), value: line } : base.line,
    calledAt: base.calledAt || fromCall(row?.timestamp || '', 'call'),
  };
}

// Context lines added to the lead's first note, so the lead says how it came in.
export function prefillNoteLines(fields) {
  if (!fields) return [];
  const when = fields.calledAt && dayjs(fields.calledAt.value).isValid() ? dayjs(fields.calledAt.value).tz(UK).format('ddd DD MMM YYYY, HH:mm') : '';
  return [
    fields.source ? `Source: ${fields.source.label || fields.source.value}` : '',
    fields.line ? `Line: ${fields.line.value}` : '',
    when ? `Called at: ${when}` : '',
    fields.callbackTime ? `Best time to call back: ${fields.callbackTime.value}` : '',
  ].filter(Boolean);
}
